import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { toast } from 'sonner';
import { Plus, Package, Trash2, ChevronRight, Eye, EyeOff, Pencil } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cf82e0fe-468c-4a43-97a9-405e8da70dca/artifacts/znleb4a2_260207%20Logo%20Galeries%20Yess5zw.png";
const MAX_ITEMS = 5;

const MyYessPage = () => {
    const { t, language } = useLanguage();
    const { api, user } = useAuth();
    const navigate = useNavigate();
    
    const [myItems, setMyItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedItem, setSelectedItem] = useState(null);
    const [editMode, setEditMode] = useState(false);

    // Scroll naar top bij laden - fix voor PWA mode
    useEffect(() => {
        // Force scroll to top immediately and after a short delay
        window.scrollTo(0, 0);
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
        
        // Also try after render
        const timer = setTimeout(() => {
            window.scrollTo(0, 0);
        }, 100);
        
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        fetchItems();
    }, []);

    const fetchItems = async () => {
        try {
            const response = await api().get('/items/my/items');
            setMyItems(response.data);
        } catch (error) {
            console.error('Failed to fetch items:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteItem = async (itemId, permanent = true) => {
        try {
            await api().delete(`/items/${itemId}?permanent=${permanent}`);
            toast.success(language === 'nl' ? 'Item verwijderd' : 'Item removed');
            setSelectedItem(null);
            fetchItems();
        } catch (error) {
            toast.error(language === 'nl' ? 'Verwijderen mislukt' : 'Failed to remove item');
        }
    };

    const handleHideItem = async (itemId) => {
        try {
            await api().put(`/items/${itemId}`, { status: 'hidden' });
            toast.success(language === 'nl' ? 'Item verborgen' : 'Item hidden');
            fetchItems();
        } catch (error) {
            toast.error(language === 'nl' ? 'Verbergen mislukt' : 'Failed to hide item');
        }
    };

    const handleRepublishItem = async (itemId) => {
        // Confirmation popup before publishing
        const confirmed = window.confirm(
            language === 'nl' 
                ? 'Weet je het zeker? Na publicatie is het item zichtbaar in Yess Items.' 
                : 'Are you sure? After publishing, the item will be visible in Yess Items.'
        );
        
        if (!confirmed) return;
        
        try {
            await api().put(`/items/${itemId}`, { status: 'available' });
            toast.success(language === 'nl' ? 'Item gepubliceerd!' : 'Item published!');
            setSelectedItem(null);
            fetchItems();
        } catch (error) {
            toast.error(language === 'nl' ? 'Publiceren mislukt' : 'Failed to publish');
        }
    };

    const handleOpenItem = (item) => {
        setSelectedItem(item);
        setEditMode(false);
    };

    const canAddMore = user?.is_admin || myItems.length < MAX_ITEMS;
    const remainingSlots = MAX_ITEMS - myItems.length;

    return (
        <div className="min-h-screen bg-white pb-24">
            {/* Fixed Header */}
            <div className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-100 safe-area-top">
                <div className="flex items-center justify-center py-4">
                    <img 
                        src={LOGO_URL} 
                        alt="Galeries Yess" 
                        className="h-8 w-auto"
                    />
                </div>
            </div>

            <div className="pt-28 px-6">
                <div className="flex items-center justify-between mb-2">
                    <h1 className="text-2xl font-heading font-light tracking-tight">
                        My Yess
                    </h1>
                    {canAddMore && (
                        <Button
                            onClick={() => navigate('/add')}
                            size="sm"
                            className="bg-black hover:bg-black/90 text-white text-xs tracking-widest uppercase font-light"
                            data-testid="add-item-button"
                        >
                            <Plus className="w-4 h-4 mr-1" />
                            Add
                        </Button>
                    )}
                </div>

                <p className="text-sm text-gray-400 mb-8">
                    {user?.is_admin ? 'Admin: Unlimited items' : `${remainingSlots} of ${MAX_ITEMS} auction item slots remaining`}
                </p>

                {loading ? (
                    <div className="flex flex-col items-center justify-center py-16">
                        <div className="animate-pulse">
                            <svg className="w-8 h-8 text-pink-400" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                            </svg>
                        </div>
                        <p className="text-xs text-gray-400 mt-3">Loading...</p>
                    </div>
                ) : myItems.length === 0 ? (
                    <div className="text-center py-20">
                        <Package className="w-16 h-16 mx-auto text-gray-200 mb-6" />
                        <p className="text-gray-400 mb-2">No auction items yet</p>
                        <p className="text-base font-semibold mb-2" style={{ color: '#F8BBD6' }}>
                            Upload up to {MAX_ITEMS} auction items
                        </p>
                        <p className="text-xs italic mb-8 px-6" style={{ color: '#F8BBD6' }}>
                            After publishing, your item will appear on the Yess Items page
                        </p>
                        <Button
                            onClick={() => navigate('/add')}
                            className="bg-black hover:bg-black/90 text-white text-sm tracking-widest uppercase font-light"
                            data-testid="add-first-item-button"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Add Your First Item
                        </Button>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {myItems.map((item) => (
                            <div
                                key={item.id}
                                className="flex gap-4 border border-gray-100 p-4"
                                data-testid={`my-item-${item.id}`}
                            >
                                {/* Image */}
                                <div className="w-20 h-24 flex-shrink-0 bg-gray-50 overflow-hidden">
                                    <img
                                        src={item.photo_processed || item.photos_original?.[0]}
                                        alt={item.title}
                                        className="w-full h-full object-cover"
                                    />
                                </div>

                                {/* Info */}
                                <div className="flex-1 min-w-0">
                                    <h3 className="font-heading text-base mb-1 truncate">
                                        {item.title}
                                    </h3>
                                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-2">
                                        {t(item.category)} · {item.size_text || 'One size'}
                                    </p>
                                    <p className="text-sm font-mono">
                                        Starting €{item.price?.toFixed(0) || '0'}
                                    </p>
                                    <div className="flex items-center gap-2 mt-2">
                                        <span className={`text-xs px-2 py-0.5 ${
                                            item.status === 'available' 
                                                ? 'bg-green-50 text-green-600' 
                                                : item.status === 'sold'
                                                    ? 'bg-gray-100 text-gray-500'
                                                    : 'bg-yellow-50 text-yellow-600'
                                        }`}>
                                            {item.status === 'available' ? 'Active' : item.status}
                                        </span>
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="flex flex-col justify-between">
                                    <button
                                        onClick={() => handleOpenItem(item)}
                                        className="p-2 hover:bg-gray-50 rounded transition-colors"
                                        data-testid={`view-item-${item.id}`}
                                    >
                                        <ChevronRight className="w-5 h-5 text-gray-300" />
                                    </button>
                                    
                                    {item.status === 'available' && (
                                        <button
                                            onClick={() => handleHideItem(item.id)}
                                            className="p-2 hover:bg-yellow-50 rounded transition-colors"
                                            title={language === 'nl' ? 'Verbergen' : 'Hide'}
                                            data-testid={`hide-item-${item.id}`}
                                        >
                                            <EyeOff className="w-4 h-4 text-yellow-500" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}

                        {/* Add More Button */}
                        {canAddMore && myItems.length > 0 && (
                            <button
                                onClick={() => navigate('/add')}
                                className="w-full py-4 border border-dashed border-gray-200 text-gray-400 hover:border-gray-300 hover:text-gray-500 transition-colors text-sm tracking-wide"
                                data-testid="add-another-item"
                            >
                                <Plus className="w-4 h-4 inline-block mr-2" />
                                {language === 'nl' ? `Nog een item toevoegen (${remainingSlots} over)` : `Add Another Item (${remainingSlots} remaining)`}
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Item Detail Sheet */}
            <Sheet open={!!selectedItem} onOpenChange={(open) => !open && setSelectedItem(null)}>
                <SheetContent side="bottom" className="h-[85vh] bg-white rounded-t-2xl">
                    {selectedItem && (
                        <div className="h-full flex flex-col">
                            <SheetHeader className="flex-shrink-0 pb-4 border-b">
                                <div className="flex items-center justify-between pr-8">
                                    <SheetTitle className="font-heading text-xl">
                                        {selectedItem.title}
                                    </SheetTitle>
                                    <button
                                        onClick={() => navigate(`/add?edit=${selectedItem.id}`)}
                                        className="p-2 hover:bg-gray-100 rounded-full"
                                        title={language === 'nl' ? 'Bewerken' : 'Edit'}
                                    >
                                        <Pencil className="w-5 h-5 text-gray-500" />
                                    </button>
                                </div>
                            </SheetHeader>
                            
                            <div className="flex-1 overflow-y-auto py-4 space-y-6">
                                {/* Item Image */}
                                <div className="aspect-square bg-gray-50 rounded-lg overflow-hidden">
                                    <img
                                        src={selectedItem.photo_processed || selectedItem.photos_original?.[0]}
                                        alt={selectedItem.title}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                
                                {/* Status Badge */}
                                <div className="flex items-center gap-2">
                                    <span className={`text-sm px-3 py-1 rounded-full ${
                                        selectedItem.status === 'available' 
                                            ? 'bg-green-50 text-green-600' 
                                            : selectedItem.status === 'sold'
                                                ? 'bg-gray-100 text-gray-500'
                                                : 'bg-yellow-50 text-yellow-600'
                                    }`}>
                                        {selectedItem.status === 'available' 
                                            ? (language === 'nl' ? 'Actief' : 'Active')
                                            : selectedItem.status === 'hidden'
                                                ? (language === 'nl' ? 'Verborgen' : 'Hidden')
                                                : selectedItem.status}
                                    </span>
                                </div>
                                
                                {/* Item Details */}
                                <div className="space-y-4">
                                    <div>
                                        <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">
                                            {language === 'nl' ? 'Categorie' : 'Category'}
                                        </p>
                                        <p className="font-medium">{t(selectedItem.category)}</p>
                                    </div>
                                    
                                    {selectedItem.size_text && (
                                        <div>
                                            <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">
                                                {language === 'nl' ? 'Maat' : 'Size'}
                                            </p>
                                            <p className="font-medium">{selectedItem.size_text}</p>
                                        </div>
                                    )}
                                    
                                    {selectedItem.materials_text && (
                                        <div>
                                            <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">
                                                {language === 'nl' ? 'Materialen' : 'Materials'}
                                            </p>
                                            <p className="text-sm">{selectedItem.materials_text}</p>
                                        </div>
                                    )}
                                    
                                    {selectedItem.care_text && (
                                        <div>
                                            <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">
                                                {language === 'nl' ? 'Verzorging' : 'Care'}
                                            </p>
                                            <p className="text-sm">{selectedItem.care_text}</p>
                                        </div>
                                    )}
                                    
                                    <div>
                                        <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">
                                            {language === 'nl' ? 'Startprijs' : 'Starting Price'}
                                        </p>
                                        <p className="text-2xl font-mono">€{selectedItem.price?.toFixed(0) || '0'}</p>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Action Buttons */}
                            <div className="flex-shrink-0 pt-4 border-t space-y-3">
                                {selectedItem.status === 'hidden' && (
                                    <Button
                                        onClick={() => handleRepublishItem(selectedItem.id)}
                                        className="w-full bg-[#F8BBD6] hover:bg-[#f0a8c8] text-foreground"
                                        data-testid="republish-item"
                                    >
                                        <Eye className="w-4 h-4 mr-2" />
                                        {language === 'nl' ? 'Opnieuw Publiceren' : 'Republish'}
                                    </Button>
                                )}
                                
                                {selectedItem.status === 'available' && (
                                    <Button
                                        onClick={() => handleHideItem(selectedItem.id)}
                                        variant="outline"
                                        className="w-full"
                                        data-testid="hide-item-btn"
                                    >
                                        <EyeOff className="w-4 h-4 mr-2" />
                                        {language === 'nl' ? 'Verbergen' : 'Hide'}
                                    </Button>
                                )}
                                
                                <Button
                                    onClick={() => {
                                        if (window.confirm(language === 'nl' 
                                            ? `Weet je zeker dat je "${selectedItem.title}" permanent wilt verwijderen?` 
                                            : `Are you sure you want to permanently delete "${selectedItem.title}"?`)) {
                                            handleDeleteItem(selectedItem.id);
                                        }
                                    }}
                                    variant="outline"
                                    className="w-full text-red-500 border-red-200 hover:bg-red-50"
                                    data-testid="delete-item-permanently"
                                >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    {language === 'nl' ? 'Permanent Verwijderen' : 'Delete Permanently'}
                                </Button>
                            </div>
                        </div>
                    )}
                </SheetContent>
            </Sheet>
        </div>
    );
};

export default MyYessPage;
